import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// A cart request must always settle so purchase controls cannot remain frozen.
const CART_REQUEST_TIMEOUT = 15000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Shows a truthful in-flight state until Shopify confirms the add.
   */
  setPending() {
    const { addToCartButton } = this.refs;
    addToCartButton.removeAttribute('data-added');
    addToCartButton.dataset.pending = 'true';
    addToCartButton.setAttribute('aria-busy', 'true');
  }

  /**
   * Clears the in-flight state without implying success.
   */
  clearPending() {
    const { addToCartButton } = this.refs;
    addToCartButton.removeAttribute('data-pending');
    addToCartButton.removeAttribute('aria-busy');
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    this.setPending();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;
    this.clearPending();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';
    if (
      animationEnabled &&
      addToCartButton.dataset.puppet !== 'true' &&
      !this.closest('.quick-add-modal')
    ) {
      this.#animateFlyToCart();
    }

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 2600);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /** @type {Event | null} */
  #queuedSubmitEvent = null;

  /** @type {AbortController | null} */
  #cartRequestAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#cartRequestAbortController?.abort();
    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  /**
   * Restores every purchase control after an add attempt settles.
   * @param {boolean} [enable=true]
   */
  #resetAddControls(enable = true) {
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    for (const container of allAddToCartContainers) {
      container.clearPending();
      if (enable) container.enable();
    }
  }

  /**
   * Shows one visible and assistive error without destroying the standard error icon.
   * @param {unknown} message
   */
  #showAddError(message) {
    const fallback = Theme.translations.add_to_cart_error || "We couldn't add this. Please try again.";
    const errorMessage = typeof message === 'string' && message.trim() ? message.trim() : fallback;
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    if (addToCartTextError) {
      let messageElement = addToCartTextError.querySelector('[data-add-to-cart-error-message]');
      if (!messageElement) {
        messageElement = document.createElement('span');
        messageElement.setAttribute('data-add-to-cart-error-message', '');
        addToCartTextError.appendChild(messageElement);
      }
      messageElement.textContent = errorMessage;
      addToCartTextError.classList.remove('hidden');
    }

    this.#setLiveRegionText(errorMessage);
    this.#timeout = setTimeout(() => {
      addToCartTextError?.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Dispatches a standardized failure while resolving the public cart promise
   * with didError so every listener settles without changing the cart count.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferredEventPromise
   * @param {unknown} message
   * @param {string} code
   * @param {string | undefined} productId
   * @param {Record<string, unknown>} [detail]
   */
  #resolveFailedAdd(deferredEventPromise, message, code, productId, detail = {}) {
    const fallback = Theme.translations.add_to_cart_error || "We couldn't add this. Please try again.";
    const errorMessage = typeof message === 'string' && message.trim() ? message.trim() : fallback;

    this.#resetAddControls();
    this.#showAddError(errorMessage);
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code,
        detail,
      })
    );

    deferredEventPromise.resolve({
      cart: null,
      detail: {
        didError: true,
        items: [],
        source: 'product-form-component',
        sourceId: this.id.toString(),
        itemCount: null,
        productId,
      },
    });
  }

  /**
   * Runs and parses an add request under one hard deadline, including a
   * response body that begins but never finishes streaming.
   * @param {RequestInfo | URL} url
   * @param {RequestInit} options
   */
  async #fetchCartRequest(url, options) {
    this.#cartRequestAbortController?.abort();
    const controller = new AbortController();
    this.#cartRequestAbortController = controller;
    let timedOut = false;

    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, CART_REQUEST_TIMEOUT);
    const abortOnDisconnect = () => controller.abort();
    this.#abortController.signal.addEventListener('abort', abortOnDisconnect, { once: true });

    try {
      const httpResponse = await fetch(url, { ...options, signal: controller.signal });
      let response;
      try {
        response = await httpResponse.json();
      } catch (error) {
        if (timedOut) throw error;
        throw new Error(Theme.translations.add_to_cart_error || "We couldn't add this. Please try again.");
      }
      return { httpResponse, response };
    } catch (error) {
      if (timedOut) {
        const timeoutError = new Error(Theme.translations.add_to_cart_error || 'The request took too long. Please try again.');
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
      this.#abortController.signal.removeEventListener('abort', abortOnDisconnect);
      if (this.#cartRequestAbortController === controller) this.#cartRequestAbortController = null;
    }
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Reads the authoritative total quantity embedded in a returned cart section.
   * Shopify's add response already contains the rendered cart drawer, so opening
   * the drawer must not wait for a second cart request.
   * @param {Record<string, string> | undefined} sections
   * @param {number} fallback
   * @returns {number}
   */
  #getCartItemCountFromSections(sections, fallback) {
    for (const html of Object.values(sections || {})) {
      if (!html) continue;
      const document = new DOMParser().parseFromString(html, 'text/html');
      const value = Number.parseInt(document.querySelector('[ref="cartItemCount"]')?.textContent || '', 10);
      if (Number.isFinite(value)) return value;
    }
    return fallback;
  }

  /**
   * Resolves the public cart event from the successful add response immediately,
   * then refreshes quantity-rule state in the background. This keeps cart
   * feedback and the drawer deterministic even when a follow-up /cart.json
   * request is slow or stalls.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferredEventPromise
   * @param {any} response
   * @param {{ itemCount: number, productId: string | undefined, fallbackItems?: any[] }} detail
   */
  #resolveSuccessfulAdd(deferredEventPromise, response, detail) {
    const responseItems = Array.isArray(response.items)
      ? response.items
      : response?.variant_id
        ? [response]
        : detail.fallbackItems || [];
    const totalItemCount = this.#getCartItemCountFromSections(response.sections, detail.itemCount);

    deferredEventPromise.resolve({
      cart: null,
      detail: {
        items: responseItems,
        source: 'product-form-component',
        sourceId: this.id.toString(),
        itemCount: totalItemCount,
        productId: detail.productId,
        sections: response.sections,
        didError: false,
      },
    });

    this.#refreshCart()
      .then((ajaxCart) => {
        if (this.#getVariantIdInput()) this.#updateCartQuantity(ajaxCart);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[product-form] Background cart synchronization deferred:', error);
        }
      });
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    const completeRoutineVariantId = this.#getCompleteRoutineVariantId();
    if (completeRoutineVariantId) {
      this.#processAddToCart(completeRoutineVariantId, 1, event, true);
      return;
    }

    if (this.#variantChangeInProgress) {
      if (!this.#queuedSubmitEvent) this.#queuedSubmitEvent = event;
      const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll('add-to-cart-component')
      );
      for (const container of allAddToCartContainers) {
        container.setPending();
        container.disable();
      }
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getCompleteRoutineVariantId() {
    const setVariantId = this.dataset.np5CompleteRoutineVariantId;
    if (!setVariantId) return undefined;
    if (this.dataset.np5SelectedOffer === 'complete-routine') return setVariantId;

    const section = this.closest('.shopify-section');
    const bars = section?.querySelector('.kaching-bundles__bars');
    const selected =
      bars?.querySelector('input[type="radio"]:checked')?.closest('.kaching-bundles__bar') ??
      bars?.querySelector('.kaching-bundles__bar--selected');

    return selected?.classList.contains('np5-kaching-option--complete') ? setVariantId : undefined;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   * @param {boolean} [cleanPayload]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event, cleanPayload = false) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    for (const container of allAddToCartContainers) {
      container.setPending();
      container.disable();
    }

    const form = this.querySelector('form');
    if (!form) {
      const message = Theme.translations.add_to_cart_error || "We couldn't add this. Please try again.";
      this.#resetAddControls();
      this.#showAddError(message);
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
      return;
    }

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.clearPending();
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage =
          errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '') ||
          Theme.translations.add_to_cart_error;
        this.#showAddError(errorMessage);
        this.dispatchEvent(
          new CartErrorEvent({
            error: errorMessage,
            code: 'MAXIMUM_EXCEEDED',
            detail: { maximum: validation.maxQuantity },
          })
        );

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    // A single-product PDP's "Complete routine" option must submit the
    // canonical Power Duo SKU. Starting with a clean payload prevents
    // app-injected component/allocation fields from splitting the same $55
    // routine differently according to the originating PDP.
    const formData = cleanPayload ? new FormData() : new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (document.getElementById('shopify-section-cart-drawer-section')) {
      cartItemComponentsSectionIds.push('cart-drawer-section');
    }
    if (cartItemComponentsSectionIds.length > 0) {
      formData.set('sections', [...new Set(cartItemComponentsSectionIds)].join(','));
      formData.set('sections_url', window.location.pathname + window.location.search);
    }

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const eventProductId =
      cleanPayload && this.dataset.np5CompleteRoutineProductId
        ? this.dataset.np5CompleteRoutineProductId
        : this.dataset.productId;
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: /** @type {string} */ (formData.get('id')),
            quantity: itemCount,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    const fetchCfg = fetchConfig('javascript', { body: formData });

    this.#fetchCartRequest(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then(({ httpResponse, response }) => {
        if (!httpResponse.ok && !response?.status) {
          response = {
            ...response,
            status: httpResponse.status,
            message: response?.message || Theme.translations.add_to_cart_error,
          };
        }
        return response;
      })
      .then(async (response) => {
        if (response.status) {
          this.#resolveFailedAdd(
            deferredEventPromise,
            response.message,
            'INVALID',
            eventProductId,
            {
              description: response.description,
              errors: response.errors,
            }
          );
          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          for (const container of allAddToCartContainers) {
            container.enable();
            container.animateAddToCart();
          }

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          this.#resolveSuccessfulAdd(deferredEventPromise, response, {
            itemCount,
            productId: eventProductId,
          });
        }
      })
      .catch((error) => {
        console.error(error);
        this.#resolveFailedAdd(
          deferredEventPromise,
          error?.message,
          'SERVICE_UNAVAILABLE',
          eventProductId
        );
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;
    let variantReady = false;

    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      // Update the button state
      if (resource == null || resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
        variantReady = Boolean(resource.id);
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available == false) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100'
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      // Fetch and update cart quantity for the new variant
      this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;

        // One click during a variant transition is replayed once, and only
        // after the new sellable variant has fully replaced the old one.
        const queuedSubmitEvent = this.#queuedSubmitEvent;
        this.#queuedSubmitEvent = null;

        if (queuedSubmitEvent && variantReady) {
          this.#processAddToCart(undefined, undefined, queuedSubmitEvent);
        } else if (queuedSubmitEvent) {
          const message = Theme.translations.add_to_cart_error || "We couldn't add this. Please try again.";
          this.#resetAddControls(false);
          this.#showAddError(message);
          this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID_MERCHANDISE_LINE' }));
        }
      }
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
